import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import parse from "date-fns/parse";
import format from "date-fns/format";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SERVER_DATE_FORMAT, SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { ClaimStatusTypes } from "@enums/claim-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { ApplicationState } from "@idw-types/state/application-state";
import { FormattedServiceAssertionPerformance } from "@idw-types/data/reports/service-assertion-performance";
import { ServiceAssertionPerformanceComparisonSearchFiltersState } from "@idw-types/state/service-assertion-performance-comparison-state";
import {
    getServiceAssertionPerformanceComparisonReferenceDataReduxRequest,
    getServiceAssertionPerformanceComparisonsReduxRequest
} from "@redux-requests/service-assertion-performance-comparison";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { setServiceAssertionPerformanceComparisonSearchFilters } from "@redux/actions/service-assertion-performance-comparison-actions";
import { initialServiceAssertionPerformanceComparisonState } from "@redux/reducers/service-assertion-performance-comparison-reducer";
import { navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ServiceAssertionPerformanceComparisonSearchForm } from "./service-assertion-performance-comparison-search-form/service-assertion-performance-comparison-search-form";
import { ServiceAssertionPerformanceComparisonTableContainer } from "./service-assertion-performance-comparison-table-container/service-assertion-performance-comparison-table-container";

export const ServiceAssertionPerformanceComparisonContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const {
        referenceData,
        searchFilters,
        searchResults,
        serviceAssertionPerformanceComparisonTableState
    } = useSelector((state: ApplicationState) => ({
        referenceData: state.serviceAssertionPerformanceComparison.referenceData,
        searchFilters: state.serviceAssertionPerformanceComparison.searchFilters,
        searchResults: state.serviceAssertionPerformanceComparison.searchResults,
        serviceAssertionPerformanceComparisonTableState: state.tableSettings.serviceAssertionPerformanceComparison
    }));

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getServiceAssertionPerformanceComparisonReferenceDataReduxRequest(dispatch)();
        }
    }, [referenceData.isLoading, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.apAssertions &&
            referenceData.assertions &&
            referenceData.policyDecisions &&
            referenceData.serviceOfferings &&
            referenceData.serviceOfferingMap &&
            referenceData.serviceOfferings &&
            referenceData.services
        );
    }, [
        referenceData.apAssertions,
        referenceData.assertions,
        referenceData.policyDecisions,
        referenceData.serviceOfferings,
        referenceData.serviceOfferingMap,
        referenceData.serviceOfferings,
        referenceData.services
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading;
    }, [referenceData.isLoading]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const newSearchFilters: ServiceAssertionPerformanceComparisonSearchFiltersState = {
                rpServiceId: searchParams.get("rpServiceId") || initialServiceAssertionPerformanceComparisonState.searchFilters.rpServiceId,
                policyDecisions: searchParams.get("policyDecisions")?.split(",") || initialServiceAssertionPerformanceComparisonState.searchFilters.policyDecisions,
                version: searchParams.get("version") || initialServiceAssertionPerformanceComparisonState.searchFilters.version,
                serviceOfferingIds: searchParams.get("serviceOfferingIds")?.split(",") || initialServiceAssertionPerformanceComparisonState.searchFilters.serviceOfferingIds,
                timeRangeType: (searchParams.get("timeRangeType") || initialServiceAssertionPerformanceComparisonState.searchFilters.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_FORMAT, new Date())
                    : initialServiceAssertionPerformanceComparisonState.searchFilters.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_FORMAT, new Date())
                    : initialServiceAssertionPerformanceComparisonState.searchFilters.endDate
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setServiceAssertionPerformanceComparisonSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: ServiceAssertionPerformanceComparisonSearchFiltersState) => {
        const { rpServiceId, policyDecisions, version, serviceOfferingIds, startDate, endDate } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE_COMPARISON));
        dispatch(setServiceAssertionPerformanceComparisonSearchFilters(newSearchFilters));

        getServiceAssertionPerformanceComparisonsReduxRequest(dispatch)(
            rpServiceId,
            version,
            policyDecisions,
            serviceOfferingIds,
            startDate,
            endDate,
            referenceData.apAssertions,
            referenceData.assertions,
            referenceData.serviceOfferings
        );
    };

    const handleClickOnTableCellLink = (claimStatusType: ClaimStatusTypes, serviceAssertionPerformance: FormattedServiceAssertionPerformance) => {
        const queryParametersObject = {
            startDate: format(startOfDay(searchFilters.startDate), SERVER_DATE_TIME_FORMAT),
            endDate: format(endOfDay(searchFilters.endDate), SERVER_DATE_TIME_FORMAT),
            timeRangeType: TimeRangeTypes.CUSTOM,
            shouldSearchOnLoad: "true",
            claimStatuses: claimStatusType,
            serviceOfferingIds: "",
            assertionIds: ""
        };

        if (serviceAssertionPerformance.apAssertion?.serviceOffering?.id) {
            queryParametersObject.serviceOfferingIds = serviceAssertionPerformance.apAssertion.serviceOffering.id;
        } else {
            delete queryParametersObject.serviceOfferingIds
        }

        if (serviceAssertionPerformance.apAssertion?.assertion?.id) {
            queryParametersObject.assertionIds = serviceAssertionPerformance.apAssertion.assertion.id;
        } else {
            delete queryParametersObject.assertionIds;
        }

        const queryParameters = createSearchParams(queryParametersObject).toString();
        navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
    };

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.serviceAssertionPerformanceComparison.header} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <ServiceAssertionPerformanceComparisonSearchForm
                        requestInProgress={searchResults.isLoading}
                        policyDecisions={referenceData.policyDecisions}
                        services={referenceData.services}
                        serviceOfferings={referenceData.serviceOfferings}
                        serviceOfferingMap={referenceData.serviceOfferingMap}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.serviceAssertionPerformances && (
                    <ServiceAssertionPerformanceComparisonTableContainer
                        handleClickOnTableCellLink={handleClickOnTableCellLink}
                        serviceAssertionPerformances={searchResults.serviceAssertionPerformances}
                        passGreenDiffRange={referenceData.passGreenDiffRange}
                        passRedDiffRange={referenceData.passRedDiffRange}
                        passYellowDiffRange={referenceData.passYellowDiffRange}
                        failGreenDiffRange={referenceData.failGreenDiffRange}
                        failRedDiffRange={referenceData.failRedDiffRange}
                        failYellowDiffRange={referenceData.failYellowDiffRange}
                        unverifiedGreenDiffRange={referenceData.unverifiedGreenDiffRange}
                        unverifiedRedDiffRange={referenceData.unverifiedRedDiffRange}
                        unverifiedYellowDiffRange={referenceData.unverifiedYellowDiffRange}
                        tableState={serviceAssertionPerformanceComparisonTableState}
                    />
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};